import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import PatientTypeSelection from '@/components/PatientTypeSelection.jsx';

/**
 * The "no preference / next available" (Wunsch-Ärzt:in) card. A presentational sibling of the named
 * `ProfessionalResult` cards: it shows the practice name + customer logo instead of a practitioner,
 * and reuses `PatientTypeSelection` with the practice-level prompt. Selecting a patient type commits
 * the no-preference anchor — the page owns that store write via `onSelectPatientType` (no business
 * logic here). Patient-type gating is chain-level, mirrored onto the synthetic `doctor` the page builds.
 */

const useStyles = makeStyles((theme) => ({
    leftCard: {
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 20,
        paddingBottom: 20,
        boxSizing: 'border-box',
    },
    selectedCard: {
        border: '2px solid #000',
    },
    logo: {
        width: '100%',
        height: '60%',
        objectFit: 'contain',
        padding: '1.5rem',
        boxSizing: 'border-box',
    },
    label: {
        fontSize: '1rem',
        textTransform: 'none',
        color: theme.palette.common.darkGrey,
        marginTop: 10,
        fontWeight: 700,
    },
    practiceName: {
        fontSize: '1rem',
        textTransform: 'none',
        textAlign: 'center',
        color: theme.palette.common.darkGrey,
        marginBottom: 10,
    },
    calendarCard: {
        maxWidth: theme.layout.contentWidth,
        display: 'block',
        padding: 30,
        borderLeft: theme.border.light,
        boxSizing: 'border-box',
    },
}));

// `PatientTypeSelection` reads these gating fields off `doctor`; the no-preference card carries the
// chain-level flags so its buttons gate identically to the named cards.
export interface NoPreferenceDoctor {
    is_for_new_patients: boolean;
    is_for_returning_patients: boolean;
    allow_new_patients?: boolean;
}

interface ChainNoPreferenceCardProps {
    label: string;
    practiceName: string;
    logoUrl?: string | null;
    prompt: string;
    doctor: NoPreferenceDoctor;
    // The Prismic block `PatientTypeSelection` reads its button labels + infotips from.
    mainData: unknown;
    loading: boolean;
    isSelected: boolean;
    onSelectPatientType: (isReturningPatient: boolean) => void;
}

const ChainNoPreferenceCard = ({
    label,
    practiceName,
    logoUrl,
    prompt,
    doctor,
    mainData,
    loading,
    isSelected,
    onSelectPatientType,
}: ChainNoPreferenceCardProps) => {
    const classes = useStyles();

    return (
        <Grid container spacing={2} style={{ marginTop: 15 }}>
            <Grid item md={4} sm={5} xs={12}>
                <div className={clsx(classes.leftCard, isSelected && classes.selectedCard)}>
                    {logoUrl ? (
                        <img className={classes.logo} src={logoUrl} alt="" aria-hidden="true" />
                    ) : (
                        <div className={classes.logo} />
                    )}
                    <Typography className={classes.label}>{label}</Typography>
                    <Typography className={classes.practiceName}>{practiceName}</Typography>
                </div>
            </Grid>
            <Grid item md={8} sm={7} xs={12}>
                <div className={classes.calendarCard}>
                    <PatientTypeSelection
                        doctor={doctor}
                        setPatientType={onSelectPatientType}
                        mainData={mainData}
                        promptOverride={prompt}
                        loading={loading}
                    />
                </div>
            </Grid>
        </Grid>
    );
};

export default ChainNoPreferenceCard;
